<script setup lang="ts">
import { computed } from 'vue'
import { useVModel, useToggle } from '@vueuse/core'
import { IconEye, IconEyeOff } from '@tabler/icons-vue'

interface Props {
  modelValue: string | number
  type?: string
  label?: string
  placeholder?: string
  error?: string
  required?: boolean
  disabled?: boolean
  helperText?: string
  showPasswordToggle?: boolean
  id?: string
}

const props = withDefaults(defineProps<Props>(), {
  type: 'text',
  label: undefined,
  placeholder: undefined,
  error: undefined,
  required: false,
  disabled: false,
  helperText: undefined,
  showPasswordToggle: false,
  id: () => `input-${Math.random().toString(36).substring(2, 11)}`
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
}>()

const model = useVModel(props, 'modelValue', emit)

const [passwordVisible, togglePasswordVisibility] = useToggle(false)

const inputType = computed(() => {
  if (props.type === 'password' && props.showPasswordToggle && passwordVisible.value) {
    return 'text'
  }
  return props.type
})
</script>

<template>
  <div class="space-y-1">
    <label
      v-if="label"
      :for="id"
      class="block text-sm font-medium text-gray-400"
    >
      {{ label }} <span v-if="required" class="text-red-500">*</span>
    </label>

    <div class="relative">
      <input
        :id="id"
        v-model="model"
        :type="inputType"
        :placeholder="placeholder"
        :required="required"
        :disabled="disabled"
        class="w-full px-3 py-2 bg-base-300 border border-white/[0.06] rounded-lg text-gray-200 font-body text-sm outline-none placeholder:text-gray-500 focus:border-amber-400/30 focus:shadow-[0_0_24px_rgba(226,179,64,0.08)] transition-all"
        :class="{
          'border-red-500 focus:border-red-500': error,
          'pr-10': type === 'password' && showPasswordToggle
        }"
        v-bind="$attrs"
      />
      <button
        v-if="type === 'password' && showPasswordToggle"
        type="button"
        class="absolute inset-y-0 right-0 pr-3 flex items-center"
        :aria-label="passwordVisible ? 'Skrýt heslo' : 'Zobrazit heslo'"
        :aria-pressed="passwordVisible"
        @click="() => togglePasswordVisibility()"
      >
        <!-- Eye open icon when password is hidden -->
        <IconEye v-if="!passwordVisible" class="w-5 h-5 text-gray-500" />
        <!-- Eye closed icon when password is visible -->
        <IconEyeOff v-else class="w-5 h-5 text-gray-500" />
      </button>
    </div>

    <p v-if="error" class="text-sm text-red-400">
      {{ error }}
    </p>
    <p v-else-if="helperText" class="text-sm text-gray-500 dark:text-gray-400">
      {{ helperText }}
    </p>
  </div>
</template>
